import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import Image from 'next/image'
import {Button} from '@/products/flexible-web-toolkit/components/button/button'
import {
	MediaCard as ClMediaCard,
	type VideoType,
} from '@/products/flexible-web-toolkit/components/media-card/media-card'
import {useTrustArcCookieValue} from '@/utilities/trustarc'
import type {Block} from '@/services/kontent-ai/types'
import {RichTextRenderer} from '@/products/flexible-web-toolkit/components/rich-text-renderer'
import {loadImageFromKontentAI} from '@/image-loader'
import {colorWithFallback} from '@/utilities/hooks/color-with-fallback'

export type MediaCardContentItem = IContentItem<{
	title: Elements.TextElement
	image: Elements.AssetsElement
	content: Elements.RichTextElement
	videoid: Elements.TextElement
	videotype: Elements.MultipleChoiceElement
	buttontext: Elements.TextElement
	cookieFallbackImage: Elements.AssetsElement
	brandColor: Elements.CustomElement
	/** @deprecated Use brandColor instead. Retained for backward compatibility. */
	variant: Elements.MultipleChoiceElement
}>

export const MediaCardBlock: Block<MediaCardContentItem> = ({
	block,
	page,
	...context
}) => {
	const trustArcCookie = useTrustArcCookieValue()
	const hideVideo = trustArcCookie === '0:' ? 'hide' : ''

	const {colorId: buttonColor} = colorWithFallback({
		blockColor: block.elements.brandColor,
		blockColorLegacy: block.elements.variant,
		pageConfig: page.elements.config[0],
		fallbackColorId: 'primary',
	})

	return (
		<ClMediaCard
			className="h-full w-84 flex-none text-black"
			data-kontent-element-codename="media_card"
			hasPlayVideoButton
			title={
				<span data-kontent-element-codename="title">
					{block.elements.title}
				</span>
			}
			videoId={hideVideo === 'hide' ? '' : block.elements.videoid}
			videoPosterFrameUrl={
				hideVideo === 'hide' ? '' : block.elements.image[0]?.url
			}
			videoType={block.elements.videotype[0]?.codename as VideoType}
		>
			{hideVideo === 'hide' &&
				(block.elements.cookieFallbackImage[0] ? (
					<Image
						alt={
							block.elements.cookieFallbackImage[0].description ??
							''
						}
						className="mb-4 h-48 w-full flex-shrink-0 object-contain"
						fill
						loader={loadImageFromKontentAI}
						src={block.elements.cookieFallbackImage[0].url}
					/>
				) : (
					<div className="order-first mb-4 h-[192px] w-full bg-blue-900" />
				))}
			<RichTextRenderer
				data-kontent-element-codename="text"
				element={block.elements.content}
				page={page}
				{...context}
			/>
			{hideVideo === 'hide' ? (
				''
			) : (
				<Button
					as="button"
					className="mt-auto w-full"
					key={block.system.id}
					variant={buttonColor ?? 'primary'}
				>
					{block.elements.buttontext || 'Play video'}
				</Button>
			)}
		</ClMediaCard>
	)
}
